import type { AllPermissionKeys } from '~/modules/settings/_generated/permissions';
import type { PermissionScope } from '~/shared/hooks/usePermission';
import { usePermissions } from '~/shared/hooks/usePermissions';

export type TeamManagementPermissions = {
  canReadFacilities: boolean;
  canReadRoles: boolean;
  canCreateRoles: boolean;
  canUpdateRoles: boolean;
  canDeleteRoles: boolean;
  canReadUsers: boolean;
  canCreateUsers: boolean;
  canUpdateUsers: boolean;
  canDeleteUsers: boolean;
  canReadPermissions: boolean;
  canUpdatePermissions: boolean;
  hasAnyReadAccess: boolean;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
};

export function useTeamManagementPermissions(
  scope?: PermissionScope,
): TeamManagementPermissions {
  const actions: AllPermissionKeys[] = [
    'facilityRead',
    'roleRead',
    'roleCreate',
    'roleUpdate',
    'roleDelete',
    'userRead',
    'userCreate',
    'userUpdate',
    'userDelete',
    'permissionRead',
    'permissionUpdate',
  ];

  const { permissions, isLoading, isError, error } = usePermissions(
    actions,
    scope,
  );

  const canReadFacilities = permissions.get('facilityRead') ?? false;
  const canReadRoles = permissions.get('roleRead') ?? false;
  const canReadUsers = permissions.get('userRead') ?? false;

  return {
    canReadFacilities,
    canReadRoles,
    canCreateRoles: permissions.get('roleCreate') ?? false,
    canUpdateRoles: permissions.get('roleUpdate') ?? false,
    canDeleteRoles: permissions.get('roleDelete') ?? false,
    canReadUsers,
    canCreateUsers: permissions.get('userCreate') ?? false,
    canUpdateUsers: permissions.get('userUpdate') ?? false,
    canDeleteUsers: permissions.get('userDelete') ?? false,
    canReadPermissions: permissions.get('permissionRead') ?? false,
    canUpdatePermissions: permissions.get('permissionUpdate') ?? false,
    hasAnyReadAccess: canReadFacilities || canReadRoles || canReadUsers,
    isLoading,
    isError,
    error,
  };
}
